import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./PasiScore.css";

const REGIONS = [
  { key: "pasi.regions.head", value: "Head" },
  { key: "pasi.regions.upperLimbs", value: "Upper Limbs" },
  { key: "pasi.regions.trunk", value: "Trunk" },
  { key: "pasi.regions.lowerLimbs", value: "Lower Limbs" },
];

const INTENSITY_ROWS = [
  { key: "pasi.intensity.redness", id: "erythema" },
  { key: "pasi.intensity.thickness", id: "induration" },
  { key: "pasi.intensity.scaling", id: "desquamation" },
];

const INTENSITY_COLS = [
  { key: "pasi.intensity.none", val: "0" },
  { key: "pasi.intensity.slight", val: "1" },
  { key: "pasi.intensity.moderate", val: "2" },
  { key: "pasi.intensity.severe", val: "3" },
  { key: "pasi.intensity.verySevere", val: "4" },
];

const areaToScore = (pct) => {
  if (pct === 0) return 0;
  if (pct <= 9) return 1;
  if (pct <= 29) return 2;
  if (pct <= 49) return 3;
  if (pct <= 69) return 4;
  if (pct <= 89) return 5;
  return 6;
};

const PasiScore = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const uid = urlParams.get("uid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const authToken = urlParams.get("authToken") || "";

  const [step, setStep] = useState(1);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState([]);
  const [areaValue, setAreaValue] = useState(0);
  const [radioValues, setRadioValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  const toggleRegion = (value) => {
    setSelectedRegions((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  };

  const buildQs = () =>
    `uuid=${uuid}&uid=${uid}&goqiiUserId=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&authToken=${authToken}&theme=${theme}&lang=${lang}`;

  const goToStep2 = () => {
    setCurrentIndex(0);
    setAreaValue(0);
    setRadioValues({});
    setFormData([]);
    setStep(2);
  };

  const currentRegion = selectedRegions[currentIndex];
  const isLastRegion = currentIndex === selectedRegions.length - 1;
  const intensityComplete = INTENSITY_ROWS.every((r) => radioValues[r.id] !== undefined);

  const advanceOrFinish = () => {
    const regionEntry = {
      [currentRegion]: {
        areaScore: areaToScore(areaValue),
        radioValue: {
          erythema: radioValues.erythema || "0",
          induration: radioValues.induration || "0",
          desquamation: radioValues.desquamation || "0",
        },
      },
    };

    if (isLastRegion) {
      if (submitting) return;
      setSubmitting(true);
      const allRegions = [...formData, regionEntry];
      lifestyleApi
        .logPasi({ goqiiUserId, regions: allRegions })
        .then((res) => {
          if (String(res?.data?.code) === "200") {
            window.location.href = `/pasidermadetails?${buildQs()}&type=pasi`;
          }
        })
        .catch((err) => console.error("logPasi:", err))
        .finally(() => setSubmitting(false));
    } else {
      setFormData((prev) => [...prev, regionEntry]);
      setCurrentIndex((i) => i + 1);
      setAreaValue(0);
      setRadioValues({});
    }
  };

  if (step === 1) {
    return (
      <>
        <section className="pasi-page">
          <div className="pasi-container">
            <div className="pasi-question-block">
              <label className="pasi-label">{t("pasi.selectRegionTitle")}</label>
              <div className="pasi-region-list">
                {REGIONS.map(({ key, value }) => (
                  <label key={value} className="pasi-region-item">
                    <input
                      type="checkbox"
                      checked={selectedRegions.includes(value)}
                      onChange={() => toggleRegion(value)}
                    />
                    <span
                      className={`pasi-checkbox-box${selectedRegions.includes(value) ? " checked" : ""}`}
                    />
                    <span className="pasi-region-label">{t(key)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>
        <section className="pasi-bottom-btn">
          <button
            type="button"
            className="pasi-btn-done"
            disabled={selectedRegions.length === 0}
            onClick={goToStep2}
          >
            {t("pasi.nextBtn")}
          </button>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="pasi-page">
        <div className="pasi-container">
          <p className="pasi-step-indicator">
            {currentIndex + 1} / {selectedRegions.length}
          </p>
          <p className="pasi-region-title">{currentRegion}</p>

          {/* Area % slider */}
          <div className="pasi-question-block">
            <label className="pasi-label">{t("pasi.areaQuestion")}</label>
            <p className="pasi-slider-percent">{areaValue}%</p>
            <input
              type="range"
              min={0}
              max={100}
              value={areaValue}
              onChange={(e) => setAreaValue(Number(e.target.value))}
              className="pasi-range-input"
              style={{
                background: `linear-gradient(to right, var(--color-primary, #35b400) ${areaValue}%, #cccccc ${areaValue}%)`,
              }}
            />
          </div>

          {/* Intensity table */}
          <div className="pasi-question-block">
            <label className="pasi-label">{t("pasi.intensityQuestion")}</label>
            <div className="pasi-table-wrap">
              <table className="pasi-table">
                <thead>
                  <tr>
                    <th className="left-col" />
                    {INTENSITY_COLS.map((c) => (
                      <th key={c.val}>{t(c.key)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {INTENSITY_ROWS.map((row) => (
                    <tr key={row.id}>
                      <td className="left-col">{t(row.key)}</td>
                      {INTENSITY_COLS.map((c) => (
                        <td key={c.val}>
                          <label className="pasi-radio-wrap">
                            <input
                              type="radio"
                              name={`pasi-${currentIndex}-${row.id}`}
                              checked={radioValues[row.id] === c.val}
                              onChange={() =>
                                setRadioValues((prev) => ({ ...prev, [row.id]: c.val }))
                              }
                            />
                            <span
                              className={`pasi-radio${radioValues[row.id] === c.val ? " checked" : ""}`}
                            />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="pasi-bottom-btn">
        <button
          type="button"
          className="pasi-btn-done"
          disabled={!intensityComplete || submitting}
          onClick={advanceOrFinish}
        >
          {isLastRegion ? t("pasi.doneBtn") : t("pasi.nextBtn")}
        </button>
      </section>
    </>
  );
};

export default PasiScore;
